/**
 * Refurbish flow: backup starego wpisu → UPDATE na WP → oznacz node jako published.
 * Rollback: przywroc stary content do WP + oznacz backup jako restored.
 *
 * Uzywany z:
 *  - publishQueueService.processPublishQueue (gdy node.match_status='matched_refurbish' + approved)
 *  - ExistingPostsPanel (manual "Odswiez wpis" per node)
 */

import { supabase } from "@/integrations/supabase/client";
import { wordpressService } from "./wordpressService";
import { logPublic, logDebug, logError } from "./contentEngine/contentLogger";
import type { WpPostRefurbishBackup } from "@/types/thematicMaps";

const PREFIX = "[REFURBISH]";

export interface RefurbishResult {
  success: boolean;
  backup_id?: string;
  wp_post_id: number;
  wp_url: string;
  error?: string;
}

interface NodeRow {
  id: string;
  map_id: string;
  title: string;
  slug: string | null;
  match_status: string | null;
  refurbish_approved_by_user: boolean | null;
  matched_wp_post_id: string | null;
  refurbish_image_strategy: string | null;
}

interface WpPostRow {
  id: string;
  wp_post_id: number;
  title: string;
  slug: string | null;
  wp_url: string | null;
  website_id: string;
  published_at: string | null;
  featured_image_url: string | null;
}

interface WebsiteRow {
  id: string;
  url: string;
  login: string | null;
  password: string | null;
  api_key: string | null;
}

interface NodeContentRow {
  node_id: string;
  seo_title: string | null;
  meta_description: string | null;
  final_html: string | null;
  featured_image_url: string | null;
  engine_stage: string | null;
}

async function setupWpCreds(websiteId: string): Promise<WebsiteRow> {
  const { data, error } = await supabase
    .from("websites")
    .select("id, url, login, password, api_key")
    .eq("id", websiteId)
    .maybeSingle();
  if (error || !data) throw new Error("Website not found");
  const w = data as WebsiteRow;
  const username = w.login;
  const password = w.api_key || w.password;
  if (!username || !password) throw new Error("Brak WP credentials dla domeny");
  wordpressService.setCredentials({
    url: w.url.replace(/\/$/, ""),
    username,
    password,
  });
  return w;
}

// =============================================
// refurbishWpPost
// =============================================

export async function refurbishWpPost(params: {
  nodeId: string;
  keepFeaturedImage?: boolean;
  createdBy?: string | null;
}): Promise<RefurbishResult> {
  const { nodeId, keepFeaturedImage = false, createdBy = null } = params;

  // 1. Load node
  const { data: nodeRaw, error: nodeErr } = await supabase
    .from("thematic_nodes")
    .select(
      "id, map_id, title, slug, match_status, refurbish_approved_by_user, matched_wp_post_id, refurbish_image_strategy",
    )
    .eq("id", nodeId)
    .maybeSingle();
  if (nodeErr || !nodeRaw) {
    return { success: false, wp_post_id: 0, wp_url: "", error: "Node not found" };
  }
  const node = nodeRaw as NodeRow;

  if (node.match_status !== "matched_refurbish") {
    return {
      success: false,
      wp_post_id: 0,
      wp_url: "",
      error: `match_status='${node.match_status}' (oczekiwano 'matched_refurbish')`,
    };
  }
  if (!node.refurbish_approved_by_user) {
    return {
      success: false,
      wp_post_id: 0,
      wp_url: "",
      error: "Refurbish not approved by user",
    };
  }
  if (!node.matched_wp_post_id) {
    return {
      success: false,
      wp_post_id: 0,
      wp_url: "",
      error: "Node has no matched_wp_post_id",
    };
  }

  // 2. Load wp_post
  const { data: wpRaw, error: wpErr } = await supabase
    .from("wp_posts")
    .select("id, wp_post_id, title, slug, wp_url, website_id, published_at, featured_image_url")
    .eq("id", node.matched_wp_post_id)
    .maybeSingle();
  if (wpErr || !wpRaw) {
    return { success: false, wp_post_id: 0, wp_url: "", error: "WP post record not found" };
  }
  const wpPost = wpRaw as WpPostRow;

  // 3. Load node_content
  const { data: ncRaw, error: ncErr } = await supabase
    .from("thematic_node_content")
    .select("node_id, seo_title, meta_description, final_html, featured_image_url, engine_stage")
    .eq("node_id", nodeId)
    .maybeSingle();
  if (ncErr || !ncRaw) {
    return {
      success: false,
      wp_post_id: wpPost.wp_post_id,
      wp_url: wpPost.wp_url || "",
      error: "Node content not found",
    };
  }
  const content = ncRaw as NodeContentRow;

  if (content.engine_stage !== "ready") {
    return {
      success: false,
      wp_post_id: wpPost.wp_post_id,
      wp_url: wpPost.wp_url || "",
      error: `Content not ready (engine_stage='${content.engine_stage}')`,
    };
  }
  if (!content.final_html) {
    return {
      success: false,
      wp_post_id: wpPost.wp_post_id,
      wp_url: wpPost.wp_url || "",
      error: "final_html is empty",
    };
  }

  logPublic(
    PREFIX,
    `Start refurbish node=${nodeId.slice(0, 8)} → wp_post=${wpPost.wp_post_id} (${wpPost.wp_url})`,
  );

  try {
    // 4. Setup WP credentials
    await setupWpCreds(wpPost.website_id);

    // 5. Fetch CURRENT WP state (safety snapshot — uwaga: context=edit)
    const currentRes = await wordpressService.fetchPost(wpPost.wp_post_id);
    if (!currentRes.success || !currentRes.data) {
      return {
        success: false,
        wp_post_id: wpPost.wp_post_id,
        wp_url: wpPost.wp_url || "",
        error: `fetchPost failed: ${currentRes.error}`,
      };
    }
    const current = currentRes.data as {
      title?: { raw?: string; rendered?: string };
      content?: { raw?: string; rendered?: string };
      excerpt?: { raw?: string; rendered?: string };
      status?: string;
      featured_media?: number;
      modified?: string;
    };

    // 6. BACKUP PRZED UPDATE
    const { data: backupRaw, error: backupErr } = await supabase
      .from("wp_post_refurbish_backups")
      .insert({
        wp_post_id: wpPost.id,
        node_id: nodeId,
        map_id: node.map_id,
        website_id: wpPost.website_id,
        old_title: current.title?.raw || current.title?.rendered || wpPost.title,
        old_content:
          current.content?.raw || current.content?.rendered || "",
        old_excerpt: current.excerpt?.raw || current.excerpt?.rendered || "",
        old_status: current.status || null,
        old_featured_media_id: current.featured_media ?? null,
        old_featured_media_url: wpPost.featured_image_url,
        old_published_at: wpPost.published_at,
        old_modified_at: current.modified || null,
        new_title: content.seo_title || node.title,
        new_word_count: null,
        new_quality_score: null,
        created_by: createdBy,
      })
      .select("id")
      .single();

    if (backupErr || !backupRaw) {
      throw new Error(`Backup insert failed: ${backupErr?.message ?? "unknown"}`);
    }
    const backupId = (backupRaw as { id: string }).id;
    logDebug(PREFIX, `Backup created id=${backupId}`);

    // 7. Decide featured_media
    let featuredMediaId = current.featured_media ?? undefined;

    if (!keepFeaturedImage && content.featured_image_url) {
      // Prob. 1: generate nowy obraz + upload do WP
      // Reuse istniejacego flow z publishQueueService (image upload helper)
      try {
        const { fetchImageAsFile } = await import("./imageGeneratorService");
        const slug = node.slug || nodeId.slice(0, 8);
        const imgFile = await fetchImageAsFile(
          content.featured_image_url,
          `refurbish-${slug}`,
        );
        if (imgFile) {
          const upl = await wordpressService.uploadMedia(imgFile, imgFile.name);
          if (upl.success && upl.data?.id) {
            featuredMediaId = upl.data.id;
            const altText = content.seo_title || node.title || "";
            if (altText) {
              await wordpressService.updateMediaAltText(featuredMediaId!, altText);
            }
            logDebug(PREFIX, `New featured image uploaded id=${featuredMediaId}`);
          } else {
            logError(PREFIX, `Featured image upload failed: ${upl.error}`);
          }
        }
      } catch (imgErr) {
        logError(
          PREFIX,
          `Image upload error — zachowuje stary obraz: ${imgErr instanceof Error ? imgErr.message : imgErr}`,
        );
      }
    }

    // 8. UPDATE WP
    // FIX 2026-05-18: WP post title = ZAWSZE oryginalny H1 (node.title).
    // seo_title → Yoast meta (osobne pole). Schema headline bierze seo_title fallback do node.title.
    const yoastMeta: Record<string, string> = {};
    if (content.seo_title) yoastMeta._yoast_wpseo_title = content.seo_title;
    if (content.meta_description) yoastMeta._yoast_wpseo_metadesc = content.meta_description;
    const updateRes = await wordpressService.updatePost(wpPost.wp_post_id, {
      title: node.title, // NIE seo_title — zostaje oryginalny H1
      content: content.final_html,
      excerpt: "",
      status: "publish",
      featured_media: featuredMediaId,
      ...(Object.keys(yoastMeta).length > 0 ? { meta: yoastMeta } : {}),
    });
    if (!updateRes.success) {
      throw new Error(`WP updatePost failed: ${updateRes.error}`);
    }
    const updated = updateRes.data as {
      postId: number;
      url: string;
      modified?: string;
      raw?: Record<string, unknown>;
    };
    logPublic(PREFIX, `WP post ${wpPost.wp_post_id} updated → ${updated.url}`);

    // 9. Update thematic_nodes
    await supabase
      .from("thematic_nodes")
      .update({
        refurbished_at: new Date().toISOString(),
        node_status: "published",
      })
      .eq("id", nodeId);

    // 10. Update thematic_node_content
    await supabase
      .from("thematic_node_content")
      .update({
        engine_stage: "published",
        published_at: new Date().toISOString(),
        wp_post_id: wpPost.wp_post_id,
        wp_post_url: updated.url,
      })
      .eq("node_id", nodeId);

    // 11. Refresh wp_posts z nowa trescia (quality reset — bedzie ponownie oceniona przy nast. sync)
    await supabase
      .from("wp_posts")
      .update({
        title: node.title, // FIX 2026-05-18: zawsze H1, NIE seo_title
        content: content.final_html,
        modified_at: updated.modified || new Date().toISOString(),
        content_fetched_at: new Date().toISOString(),
        quality_score: null,
        quality_assessed_at: null,
      })
      .eq("id", wpPost.id);

    // 12. Update backup z WP response
    await supabase
      .from("wp_post_refurbish_backups")
      .update({ wp_rest_response: updated.raw ?? updated })
      .eq("id", backupId);

    return {
      success: true,
      backup_id: backupId,
      wp_post_id: wpPost.wp_post_id,
      wp_url: updated.url || wpPost.wp_url || "",
    };
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    logError(PREFIX, `Failed for node ${nodeId}: ${msg}`);
    return {
      success: false,
      wp_post_id: wpPost.wp_post_id,
      wp_url: wpPost.wp_url || "",
      error: msg,
    };
  }
}

// =============================================
// rollbackRefurbish
// =============================================

export async function rollbackRefurbish(
  backupId: string,
  restoredBy?: string | null,
): Promise<RefurbishResult> {
  const { data: backupRaw, error: bErr } = await supabase
    .from("wp_post_refurbish_backups")
    .select(
      "id, wp_post_id, node_id, map_id, website_id, old_title, old_content, old_excerpt, old_status, old_featured_media_id, old_featured_media_url, restored_at",
    )
    .eq("id", backupId)
    .maybeSingle();
  if (bErr || !backupRaw) {
    return { success: false, wp_post_id: 0, wp_url: "", error: "Backup not found" };
  }
  const backup = backupRaw as Pick<
    WpPostRefurbishBackup,
    | "id"
    | "wp_post_id"
    | "node_id"
    | "map_id"
    | "website_id"
    | "old_title"
    | "old_content"
    | "old_excerpt"
    | "old_status"
    | "old_featured_media_id"
    | "old_featured_media_url"
    | "restored_at"
  >;

  if (backup.restored_at) {
    return {
      success: false,
      wp_post_id: 0,
      wp_url: "",
      error: `Backup already restored at ${backup.restored_at}`,
    };
  }

  // Load wp_post
  const { data: wpRaw } = await supabase
    .from("wp_posts")
    .select("id, wp_post_id, wp_url, website_id, title")
    .eq("id", backup.wp_post_id)
    .maybeSingle();
  if (!wpRaw) {
    return { success: false, wp_post_id: 0, wp_url: "", error: "WP post record not found" };
  }
  const wp = wpRaw as {
    id: string;
    wp_post_id: number;
    wp_url: string | null;
    website_id: string;
    title: string;
  };

  logPublic(PREFIX, `Rollback wp_post=${wp.wp_post_id} from backup=${backupId}`);

  try {
    if (!backup.website_id) {
      throw new Error("Backup has no website_id");
    }
    await setupWpCreds(backup.website_id);

    const restoreRes = await wordpressService.updatePost(wp.wp_post_id, {
      title: backup.old_title,
      content: backup.old_content,
      excerpt: backup.old_excerpt ?? "",
      status: ((backup.old_status ?? "publish") as "publish" | "draft" | "private" | "pending"),
      featured_media: backup.old_featured_media_id ?? undefined,
    });
    if (!restoreRes.success) {
      throw new Error(`WP updatePost (rollback) failed: ${restoreRes.error}`);
    }

    // Mark backup restored
    await supabase
      .from("wp_post_refurbish_backups")
      .update({
        restored_at: new Date().toISOString(),
        restored_by: restoredBy ?? null,
      })
      .eq("id", backupId);

    // Refresh wp_posts back
    await supabase
      .from("wp_posts")
      .update({
        title: backup.old_title,
        content: backup.old_content,
        modified_at: new Date().toISOString(),
        quality_score: null,
        quality_assessed_at: null,
      })
      .eq("id", wp.id);

    // Revert node state
    await supabase
      .from("thematic_nodes")
      .update({
        refurbished_at: null,
        refurbish_approved_by_user: false,
        refurbish_approved_at: null,
      })
      .eq("id", backup.node_id);

    logPublic(PREFIX, `Rollback success wp_post=${wp.wp_post_id}`);

    return {
      success: true,
      backup_id: backupId,
      wp_post_id: wp.wp_post_id,
      wp_url: wp.wp_url || "",
    };
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    logError(PREFIX, `Rollback failed: ${msg}`);
    return {
      success: false,
      wp_post_id: wp.wp_post_id,
      wp_url: wp.wp_url || "",
      error: msg,
    };
  }
}

// =============================================
// getRefurbishHistory
// =============================================

export async function getRefurbishHistory(
  mapId: string,
): Promise<
  Array<
    WpPostRefurbishBackup & {
      wp_post_url?: string | null;
      node_title?: string | null;
    }
  >
> {
  const { data } = await supabase
    .from("wp_post_refurbish_backups")
    .select(
      `
      *,
      wp_post:wp_posts(wp_url),
      node:thematic_nodes(title)
    `,
    )
    .eq("map_id", mapId)
    .order("backup_at", { ascending: false });

  return ((data || []) as Array<
    WpPostRefurbishBackup & {
      wp_post?: { wp_url: string | null } | null;
      node?: { title: string } | null;
    }
  >).map((e) => ({
    ...e,
    wp_post_url: e.wp_post?.wp_url ?? null,
    node_title: e.node?.title ?? null,
  }));
}
